import { createSelector } from '@reduxjs/toolkit';
import { createCachedSelector } from 're-reselect';

import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { Permission } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import type { CommonAclsPrefix } from '../../ducks/common_acls/types';
import type { AppState } from '../../store/types';
import {
  allGrantedPermissions,
  type PermissionValues,
} from '../../types/common';
import {
  getEntityUserRoles,
  getPermissionsRolesMerge,
} from '../../utils/get_entity_roles';
import { selectUserRoles } from '../../utils/get_user_roles';
import permissionsArrayToObject from '../../utils/permissions_array_to_object';

const getPermissionsData = (state: AppState, aclsType: CommonAclsPrefix) =>
  state.acls[aclsType];

export const selectInternalProps = createSelector(
  getPermissionsData,
  selectUserRoles,
  (permissionsData, userRoles) => ({
    permissionsData,
    userRoles,
  }),
);

const getAclsPrefix = (
  _state: AppState,
  aclsPrefix: CommonAclsPrefix,
): CommonAclsPrefix => aclsPrefix;

const getEntityPermissions = (
  state: AppState,
  aclsType: CommonAclsPrefix,
  entityId: string | undefined,
): PermissionValues | undefined =>
  entityId ? state.acls[aclsType].entityCurrentUserAcls[entityId] : undefined;

const getSearchItemPermissions = (
  state: AppState,
  aclsType: CommonAclsPrefix,
  entityId: string | undefined,
): Permission[] | undefined => {
  if (
    entityId &&
    (aclsType === OBJECT_TYPE_ASSETS || aclsType === OBJECT_TYPE_COLLECTIONS)
  ) {
    return state.entities.search_items[entityId]?.permissions;
  }
  return undefined;
};

const getCacheKey = (
  _state: AppState,
  aclsPrefix: CommonAclsPrefix,
  entityId: string | undefined,
) => `${aclsPrefix}:${entityId}`;

const getIsAdmin = (state: AppState) => state.authReducer.userData.is_admin;

/**
 * Returns permissions for entity with fallback to search item permissions.
 */
export const selectPermissions = createCachedSelector(
  getEntityPermissions,
  getSearchItemPermissions,
  getIsAdmin,
  (entityPermissions, searchItemPermissions, isAdmin) => {
    if (isAdmin) {
      return allGrantedPermissions;
    }
    const permissions = searchItemPermissions
      ? permissionsArrayToObject(searchItemPermissions)
      : undefined;
    return entityPermissions ?? permissions;
  },
)(getCacheKey);

/**
 * Returns entity permissions merged with user roles. That means that if user has
 * a permission for entity, but doesn't have a corresponding role, the permission
 * won't be granted. For example, if user has a permission to delete an asset, but
 * doesn't have canDeleteAssets role, the permission won't be granted.
 */
export const selectMergedPermissions = createCachedSelector(
  selectUserRoles,
  getAclsPrefix,
  selectPermissions,
  (userRoles, aclsType, permissions) =>
    getPermissionsRolesMerge(
      aclsType,
      userRoles,
      permissions,
      getEntityUserRoles(userRoles)[aclsType],
    ),
)(getCacheKey);

export default selectMergedPermissions;
